// v. 1.0
// BlogModule.jsx

import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import { generateBlog } from "../utils/api";
import { safeCapture } from "../utils/analytics";
import { getProfile } from "../utils/profile";
import { getActiveBrand, listOffers, getDefaultAudienceText } from "../utils/vault";
import BrandVoiceIndicator from "./BrandVoiceIndicator";

// Markdown -> rendered HTML
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function BlogModule() {
  const { user, loading: authLoading } = useAuth();
  const location = useLocation();

  const [brandProfile, setBrandProfile] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [vaultBrand, setVaultBrand] = useState(null);
  const [vaultOffers, setVaultOffers] = useState([]);
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [vaultAudienceText, setVaultAudienceText] = useState("");

  const draftKey = useMemo(() => {
    return user?.id ? `blogDraft:${user.id}` : "blogDraft:guest";
  }, [user?.id]);

  const [step, setStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    topic: "",
    keywords: "",
    featured: false,
  });

  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!user) return;

    setUserEmail(user.email || "");

    let isMounted = true;

    (async () => {
      try {
        const profile = await getProfile();
        if (!isMounted) return;
        setBrandProfile(profile);
      } catch (err) {
        console.warn("Could not load brand profile in BlogModule:", err);
      }

      try {
        const brand = await getActiveBrand();
        if (!isMounted) return;
        setVaultBrand(brand);
        if (brand) {
          setVaultOffers(await listOffers(brand.id));
          setVaultAudienceText(await getDefaultAudienceText(brand));
        }
      } catch (err) {
        console.warn("Could not load vault brand in BlogModule:", err);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [user]);

  // Restore draft
  useEffect(() => {
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed?.formData) setFormData(parsed.formData);
        if (parsed?.step) setStep(parsed.step);
        if (parsed?.result) setResult(parsed.result);
      } catch {
        // ignore bad local draft
      }
    }
  }, [draftKey]);

  // Save draft
  useEffect(() => {
    localStorage.setItem(
      draftKey,
      JSON.stringify({ formData, step, result })
    );
  }, [draftKey, formData, step, result]);

  const reset = () => {
    setStep(1);
    setResult(null);
    setError(null);
    setIsGenerating(false);
    localStorage.removeItem(draftKey);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied!");
    } catch {
      alert("Could not copy to clipboard.");
    }
  };

  const getSnarkLabel = (level) => {
    if (level < 0.2) return "Polite";
    if (level < 0.45) return "Light Edge";
    if (level < 0.7) return "Direct";
    return "Sharp";
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.topic.trim()) {
      setError("Please enter a single blog topic.");
      return;
    }

    setIsGenerating(true);

    const selectedOffer = vaultOffers.find((o) => o.id === selectedOfferId);

    const payload = {
      topic: formData.topic.trim(),
      keywords: formData.keywords.trim(),
      featured: !!formData.featured,
      email: user?.email || "",
      user_id: user?.id,
      mode: "blog",
      brand_voice: brandProfile?.brand_voice ?? "",
      snark_level: brandProfile?.snark_level ?? 0.3,
      company: vaultBrand?.name ?? "",
      audience: vaultAudienceText,
      offer_context: selectedOffer?.description ?? "",
    };

    const startedAt = Date.now();

    try {
      const data = await generateBlog(payload);
      setResult(data);
      setStep(2);

      safeCapture("generate_blog", {
        route: window.location.pathname,
        success: !!data,
        featured: !!formData.featured,
        ms: Date.now() - startedAt,
      });
    } catch (err) {
      const msg =
        err?.message || "Something went wrong generating the blog post.";
      setError(msg);

      safeCapture("generate_blog_failed", {
        route: window.location.pathname,
        message: String(msg).slice(0, 120),
        ms: Date.now() - startedAt,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Flexible render: supports either a structured JSON response OR a single blob string
  const title = result?.title ?? "";
  const meta = result?.meta_description ?? result?.metaDescription ?? "";
  const content = result?.content ?? result?.blog ?? result?.blog_content ?? "";
  const imagePrompts =
    result?.image_prompts ??
    result?.imagePrompts ??
    (Array.isArray(result?.images) ? result.images : []);

  const fullCopyBlock = [
    title ? `Title: ${title}` : "",
    meta ? `Meta Description: ${meta}` : "",
    content ? `Blog Content:\n${content}` : "",
    imagePrompts?.length
      ? `Image Prompts:\n${imagePrompts
          .map((p, i) => `${i + 1}. ${p}`)
          .join("\n")}`
      : "",
  ]
    .filter(Boolean)
    .join("\n\n");

  const mdComponents = {
    h1: ({ children }) => (
      <h1 className="text-2xl font-bold text-gray-900 mb-3 mt-2">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-xl font-semibold text-gray-900 mb-2 mt-5">{children}</h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4">{children}</h3>
    ),
    p: ({ children }) => (
      <p className="text-gray-800 leading-7 mb-3">{children}</p>
    ),
    ul: ({ children }) => (
      <ul className="list-disc pl-6 space-y-1 mb-3 text-gray-800">{children}</ul>
    ),
    ol: ({ children }) => (
      <ol className="list-decimal pl-6 space-y-1 mb-3 text-gray-800">{children}</ol>
    ),
    li: ({ children }) => <li className="leading-7">{children}</li>,
    strong: ({ children }) => (
      <strong className="font-semibold text-gray-900">{children}</strong>
    ),
    em: ({ children }) => <em className="italic">{children}</em>,
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-700 my-4">
        {children}
      </blockquote>
    ),
  };

  if (authLoading) {
    return <div style={{ padding: 24, opacity: 0.8 }}>Loading…</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-2">
        Blog Writer
      </h2>
      <p className="text-sm text-gray-600 mb-6">
        One topic in, one clear post out. (DobieCore loves singularity.)
      </p>

      <BrandVoiceIndicator
        brandProfile={brandProfile}
        userEmail={userEmail}
        getSnarkLabel={getSnarkLabel}
      />

      {step === 1 && (
        <form onSubmit={handleGenerate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Blog topic (single idea) *
            </label>
            <input
              type="text"
              value={formData.topic}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, topic: e.target.value }))
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sabre-blue focus:border-transparent"
              placeholder="e.g., Why one clear topic improves AI output"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Tip: don&apos;t stack multiple concepts. If it needs “and,” split it into 2 posts.
            </p>
          </div>

          {vaultOffers.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mention one of your offers (optional)
              </label>
              <select
                value={selectedOfferId}
                onChange={(e) => setSelectedOfferId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sabre-blue focus:border-transparent"
              >
                <option value="">— None —</option>
                {vaultOffers.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Only mentioned if it fits the topic naturally — this won&apos;t turn the post into an ad.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Keywords (optional)
            </label>
            <input
              type="text"
              value={formData.keywords}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, keywords: e.target.value }))
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sabre-blue focus:border-transparent"
              placeholder="e.g., web development, AI prompts, content systems"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="featured"
              type="checkbox"
              checked={formData.featured}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, featured: e.target.checked }))
              }
              className="h-4 w-4"
            />
            <label htmlFor="featured" className="text-sm text-gray-700">
              Longer post (~1000 words)
            </label>
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isGenerating}
            className="w-full bg-sabre-blue text-white py-3 rounded-lg font-medium hover:bg-sabre-blue/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isGenerating ? "Generating..." : "Create Blog Post"}
          </button>
        </form>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => copyToClipboard(fullCopyBlock)}
              className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              Copy All
            </button>
            <button
              type="button"
              onClick={reset}
              className="flex-1 bg-sabre-blue text-white py-3 rounded-lg font-medium hover:bg-sabre-blue/90 transition-colors"
            >
              Start Over
            </button>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Title</h3>
            <p className="text-gray-800 whitespace-pre-wrap">{title || "—"}</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Meta Description
            </h3>
            <p className="text-gray-800 whitespace-pre-wrap">{meta || "—"}</p>
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="flex justify-between items-start mb-2">
              <h3 className="text-lg font-semibold text-gray-800">Blog Content</h3>
              {content && (
                <button
                  onClick={() => copyToClipboard(content)}
                  className="text-sm text-slate-900 hover:text-slate-700 font-medium"
                >
                  Copy
                </button>
              )}
            </div>

            {content ? (
              <div className="bg-white rounded-md border border-gray-200 p-4">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={mdComponents}
                >
                  {content}
                </ReactMarkdown>
              </div>
            ) : (
              <p className="text-gray-800 whitespace-pre-wrap">
                {String(result || "—")}
              </p>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Image Prompts
            </h3>
            {Array.isArray(imagePrompts) && imagePrompts.length ? (
              <ul className="list-disc pl-5 space-y-1 text-gray-800">
                {imagePrompts.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-700">—</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}